import { InlineMath } from "react-katex";
import { useState, useEffect, useRef } from "react";
import ShowMaterialMediaByContentType from "./ShowMaterialMediaByContentType";
import { IFillInTheBlanksActivityMaterialProps } from "../../../properties/ActivityProperties";
import InputWithSpecialExpression from "../../texts/InputWithSpecialExpression";
import i18n from "../../../language/i18n";
import { constructInputWithSpecialExpressionList, checkAnswerState } from "../../../utilization/CatchtivityUtilization";
import { shuffleArray } from "../../../utilization/AppUtilization";
import { fillInTheBlanksActivityMaterialContentStyles } from "./FillInTheBlanksActivityMaterialContentStyles";
import { resolveMaterialMediaType } from "../../../utilization/StorageUtilization";

const FillInTheBlanksActivityMaterialContent = ({
  uniqueValue,
  answerMap,
  optionList,
  materialMap,
  contentMap,
  checkCanAnswerQuestion,
  onChange,
  isPreview,
  showCorrectAnswer,
}: IFillInTheBlanksActivityMaterialProps) => {
  // Options shuffled once PER ACTIVITY (content-keyed) — the parent rebuilds
  // the optionList array every render, so a reference-keyed effect would
  // re-shuffle the word bank on every interaction.
  const optionListKey = JSON.stringify(optionList);
  const [shuffleOptionList, setShuffleOptionList] = useState<any[]>(() => shuffleArray(optionList));
  const shuffleKeyRef = useRef<string>(optionListKey);
  useEffect(() => {
    if (shuffleKeyRef.current === optionListKey) return;
    shuffleKeyRef.current = optionListKey;
    setShuffleOptionList(shuffleArray(optionList));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionListKey]);
  const [selectedOption, setSelectedOption] = useState<any>(null);
  const [draggedOption, setDraggedOption] = useState<any>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<string | null>(null);
  const [draggedElement, setDraggedElement] = useState<HTMLElement | null>(null);
  const [displayAnswerMap, setDisplayAnswerMap] = useState<any>(answerMap);
  const dragElementRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [touchPosition, setTouchPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    if (!draggedOption) return;
    const preventScroll = (e: TouchEvent) => { e.preventDefault(); };
    document.addEventListener("touchmove", preventScroll, { passive: false });
    return () => { document.removeEventListener("touchmove", preventScroll); };
  }, [draggedOption]);

  useEffect(() => {
    if (showCorrectAnswer) {
      const correctAnswerMap: any = {};
      Object.keys(materialMap).forEach((materialMapKey) => {
        correctAnswerMap[materialMapKey] = JSON.parse(materialMap[materialMapKey])[0];
      });
      setDisplayAnswerMap(correctAnswerMap);
    } else {
      setDisplayAnswerMap(answerMap);
    }
  }, [showCorrectAnswer, answerMap, materialMap]);

  const checkAnswerProvided = (currentAnswerMap: Record<string, any>, option: string): boolean => {
    return Object.keys(currentAnswerMap).findIndex((key) => currentAnswerMap[key] === option) !== -1;
  };

  // ── Mouse drag ───────────────────────────────────────────────────────────
  const handleMouseDown = (e: React.MouseEvent, option: any): void => {
    e.preventDefault();
    setDraggedOption(option);
    setSelectedOption(null);
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent): void => {
    if (!draggedOption) return;
    setMousePosition({ x: e.clientX, y: e.clientY });
    const elementUnder = document.elementFromPoint(e.clientX, e.clientY);
    const dropZone = elementUnder?.closest("[data-drop-zone]");
    setDropTargetIndex(dropZone ? dropZone.getAttribute("data-drop-zone") : null);
  };

  const handleMouseUp = (): void => {
    if (dropTargetIndex !== null && draggedOption !== null) {
      onChange(answerMap, dropTargetIndex, draggedOption);
    }
    setDraggedOption(null);
    setDropTargetIndex(null);
    setMousePosition({ x: 0, y: 0 });
  };

  // ── Touch drag ───────────────────────────────────────────────────────────
  const handleTouchStart = (e: React.TouchEvent, option: any, element: HTMLElement): void => {
    e.preventDefault();
    const touch = e.touches[0];
    setDraggedOption(option);
    setDraggedElement(element);
    setTouchPosition({ x: touch.clientX, y: touch.clientY });
    setSelectedOption(null);
  };

  const handleTouchMove = (e: React.TouchEvent): void => {
    if (!draggedOption) return;
    const touch = e.touches[0];
    setTouchPosition({ x: touch.clientX, y: touch.clientY });
    const elementUnder = document.elementFromPoint(touch.clientX, touch.clientY);
    const dropZone = elementUnder?.closest("[data-drop-zone]");
    setDropTargetIndex(dropZone ? dropZone.getAttribute("data-drop-zone") : null);
  };

  const handleTouchEnd = (): void => {
    if (dropTargetIndex !== null && draggedOption !== null) {
      onChange(answerMap, dropTargetIndex, draggedOption);
    }
    setDraggedOption(null);
    setDropTargetIndex(null);
    setDraggedElement(null);
    setTouchPosition({ x: 0, y: 0 });
  };

  // ── Tap-to-place ─────────────────────────────────────────────────────────
  const handleSelectOption = (option: any): void => {
    setSelectedOption(option);
    setDraggedOption(null);
  };

  const handleDropZoneClick = (index: string): void => {
    if (selectedOption !== null) {
      onChange(answerMap, index, selectedOption);
      setSelectedOption(null);
    }
  };

  // ── Blank render helper ──────────────────────────────────────────────────
  const renderBlank = (materialKey: string) => {
    const currentAnswer = displayAnswerMap[materialKey];
    const canAnswer = checkCanAnswerQuestion();

    // Derive answer state for preview/graded mode. Design: a blank left EMPTY
    // also counts as wrong once checked ("—" struck + correct answer below).
    const learnerAnswerState =
      !isPreview
        ? null
        : !currentAnswer || currentAnswer === "" || currentAnswer === "DEFAULT_OPTION"
          ? "INCORRECT"
          : checkAnswerState(JSON.parse(materialMap[materialKey]), currentAnswer);

    const isDropTarget = dropTargetIndex === materialKey;
    const hasPicked = selectedOption !== null;
    const isFilled = canAnswer
      ? !!currentAnswer && currentAnswer !== "" && currentAnswer !== "DEFAULT_OPTION"
      : !!currentAnswer && currentAnswer !== "";

    if (resolveMaterialMediaType(currentAnswer, contentMap.type) === "TEXT") {
      if (!canAnswer) {
        // Read-only: show answer text with math/bold/underline rendering
        return (
          <span key={materialKey} className="fitb2-readonly-answer">
            <span className="fitb2-blank-num">{parseFloat(materialKey) + 1}</span>
            {constructInputWithSpecialExpressionList(currentAnswer || "").map((part, pi) => (
              <span
                key={pi}
                className={`${part.isBold ? "mc-bold" : ""} ${part.isUnderline ? "mc-underline" : ""}`}
              >
                {part.isEquation ? (
                  <InlineMath math={part.value} />
                ) : (
                  part.value
                )}
              </span>
            ))}
          </span>
        );
      }

      if (learnerAnswerState === "CORRECT") {
        return (
          <span
            key={materialKey}
            className="fitb2-blank filled is-correct"
            data-drop-zone={materialKey}
            onClick={() => handleDropZoneClick(materialKey)}
          >
            <InputWithSpecialExpression value={currentAnswer} showSpecialOnly={false} />
          </span>
        );
      }

      if (learnerAnswerState === "INCORRECT") {
        const correctAnswer = JSON.parse(materialMap[materialKey])[0];
        const shownWrongValue =
          currentAnswer && currentAnswer !== "DEFAULT_OPTION"
            ? currentAnswer
            : "—";
        return (
          <span key={materialKey} className="fitb2-blank-col">
            <span
              className={`fitb2-blank filled ${
                isFilled ? "is-wrong" : "is-blank"
              }`}
              data-drop-zone={materialKey}
              onClick={() => handleDropZoneClick(materialKey)}
            >
              <InputWithSpecialExpression value={shownWrongValue} showSpecialOnly={false} />
            </span>
            <span className="fitb2-fix-below">
              {"✓"}{" "}
              <InputWithSpecialExpression
                value={correctAnswer}
                showSpecialOnly={false}
              />
            </span>
          </span>
        );
      }

      if (isFilled) {
        return (
          <span
            key={materialKey}
            className={`fitb2-blank filled${isDropTarget ? " is-target" : ""}`}
            data-drop-zone={materialKey}
            onMouseEnter={() => draggedOption && setDropTargetIndex(materialKey)}
            onMouseLeave={() => setDropTargetIndex(null)}
            onClick={(e) => {
              e.stopPropagation();
              // clicking a filled blank returns the word to bank
              onChange(answerMap, materialKey, "");
            }}
          >
            <InputWithSpecialExpression value={currentAnswer} showSpecialOnly={false} />
          </span>
        );
      }

      return (
        <span
          key={materialKey}
          className={`fitb2-blank${isDropTarget || hasPicked ? " is-target" : " empty"}`}
          data-drop-zone={materialKey}
          onMouseEnter={() => draggedOption && setDropTargetIndex(materialKey)}
          onMouseLeave={() => setDropTargetIndex(null)}
          onClick={() => handleDropZoneClick(materialKey)}
        >
          {hasPicked ? i18n.t("please_drop_here") : `${parseFloat(materialKey) + 1}.`}
        </span>
      );
    }

    // ── IMAGE / MEDIA blank, GRADED ──────────────────────────────────────
    // The drag-and-drop slot below has no way to show the key: a wrong image
    // was only outlined red and an empty one stayed an empty drop zone, so the
    // correct option was never on screen. Graded, the pick is framed green /
    // red (amber when left blank) and, unless it was right, the correct option
    // is shown under it with a "Doğru cevap" tag.
    if (isPreview && !showCorrectAnswer) {
      const correctAnswer = JSON.parse(materialMap[materialKey])[0];
      const hasPick = Boolean(currentAnswer && currentAnswer !== "DEFAULT_OPTION");
      const imgState = hasPick
        ? learnerAnswerState === "CORRECT"
          ? "is-correct"
          : "is-wrong"
        : "is-blank";
      const renderMedia = (src: string, key: string) => (
        <ShowMaterialMediaByContentType
          key={key}
          contentType={resolveMaterialMediaType(src, contentMap.type)}
          src={src}
          canFullScreen={true}
        />
      );
      return (
        <span key={materialKey} className="fitb2-blank-col">
          <span className={`fitb2-img-blank filled is-graded ${imgState}`}>
            {hasPick ? (
              renderMedia(currentAnswer, `${uniqueValue}-${materialKey}-pick`)
            ) : (
              <span className="fitb2-img-empty">—</span>
            )}
          </span>
          {imgState !== "is-correct" ? (
            <span className="fitb2-img-key">
              <span className="fitb2-img-key-tag">{i18n.t("correct_label")}</span>
              <span className="fitb2-img-blank filled is-graded is-key">
                {renderMedia(correctAnswer, `${uniqueValue}-${materialKey}-key`)}
              </span>
            </span>
          ) : null}
        </span>
      );
    }

    // ── IMAGE / MEDIA blank ──────────────────────────────────────────────
    if (!canAnswer) {
      if (!currentAnswer) return <span key={materialKey} className="fitb2-img-blank" />;
      return (
        <span key={materialKey} className="fitb2-img-blank filled">
          <ShowMaterialMediaByContentType
            key={`${uniqueValue}-ro-${materialKey}`}
            contentType={resolveMaterialMediaType(currentAnswer, contentMap.type)}
            src={currentAnswer}
            canFullScreen={true}
          />
        </span>
      );
    }

    if (isFilled) {
      const imgState = learnerAnswerState === "CORRECT"
        ? " is-correct"
        : learnerAnswerState === "INCORRECT"
          ? " is-wrong"
          : "";
      return (
        <span
          key={materialKey}
          className={`fitb2-img-blank filled${imgState}`}
          data-drop-zone={materialKey}
          onMouseEnter={() => draggedOption && setDropTargetIndex(materialKey)}
          onMouseLeave={() => setDropTargetIndex(null)}
          onClick={(e) => { e.stopPropagation(); onChange(answerMap, materialKey, ""); }}
        >
          <ShowMaterialMediaByContentType
            key={`${uniqueValue}-${materialKey}`}
            contentType={resolveMaterialMediaType(currentAnswer, contentMap.type)}
            src={currentAnswer}
            canFullScreen={true}
          />
        </span>
      );
    }

    return (
      <span
        key={materialKey}
        className={`fitb2-img-blank${isDropTarget || hasPicked ? " is-target" : ""}`}
        data-drop-zone={materialKey}
        onMouseEnter={() => draggedOption && setDropTargetIndex(materialKey)}
        onMouseLeave={() => setDropTargetIndex(null)}
        onClick={() => handleDropZoneClick(materialKey)}
      >
        <span style={{ fontSize: "var(--fs-sm)", color: "var(--text-muted)", padding: "4px 8px", display: "block" }}>
          {hasPicked ? i18n.t("please_drop_here") : `${parseFloat(materialKey) + 1}.`}
        </span>
      </span>
    );
  };

  const sortedMaterialKeys = Object.keys(displayAnswerMap).sort(
    (a, b) => parseFloat(a) - parseFloat(b)
  );

  const canAnswer = checkCanAnswerQuestion();

  const dragGhostOption = draggedOption || null;
  const ghostX = mousePosition.x > 0 ? mousePosition.x : touchPosition.x > 0 ? touchPosition.x : 0;
  const ghostY = mousePosition.y > 0 ? mousePosition.y : touchPosition.y > 0 ? touchPosition.y : 0;

  return (
    <div
      className="fitb2-root mc-root"
      style={{ touchAction: draggedOption ? "none" : "auto" }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      <style>{fillInTheBlanksActivityMaterialContentStyles}</style>

      {/* Floating drag ghost (mouse + touch) */}
      {dragGhostOption && ghostX > 0 && (
        <div
          className="fitb2-drag-ghost"
          style={{ left: `${ghostX}px`, top: `${ghostY}px`, transform: "translate(-50%, -50%)" }}
        >
          {resolveMaterialMediaType(dragGhostOption, contentMap.type) === "TEXT" ? (
            <span className="fitb2-ghost-chip">
              <InputWithSpecialExpression value={dragGhostOption} showSpecialOnly={false} />
            </span>
          ) : (
            <span className="mc-ord-chip-media">
              <ShowMaterialMediaByContentType
                key={uniqueValue}
                contentType={resolveMaterialMediaType(dragGhostOption, contentMap.type)}
                src={dragGhostOption}
                canFullScreen={false}
              />
            </span>
          )}
        </div>
      )}

      {/* ── Blank slots rendered as a flowing sentence row ── */}
      <div className="fitb2-sentence">
        {sortedMaterialKeys.map((materialKey) => renderBlank(materialKey))}
      </div>

      {/* ── Word bank ── */}
      {/* The bank is a solving affordance; a graded view has nothing to place. */}
      {canAnswer && !isPreview && (
        <>
          <div className="fitb2-bank-label">{i18n.t("word_bank") || "Kelime Havuzu"}</div>
          <div className="fitb2-bank">
            {shuffleOptionList.map((option, index) => {
              const isUsed = checkAnswerProvided(displayAnswerMap, option);
              const isPicked = selectedOption === option;
              const isDragging = draggedOption === option;
              const optionMediaType = resolveMaterialMediaType(option, contentMap.type);
              if (optionMediaType !== "TEXT") {
                if (isUsed) {
                  return (
                    <ShowMaterialMediaByContentType
                      key={`${uniqueValue}-${index}`}
                      contentType={resolveMaterialMediaType(option, contentMap.type)}
                      src={option}
                      canFullScreen={true}
                    />
                  );
                }
                return (
                  <div
                    key={index}
                    ref={isDragging ? dragElementRef : null}
                    className={`${isDragging ? "mc-ord-dragging" : isPicked ? "mc-ord-selected" : ""} mc-mat-drag-item`}
                    style={{ touchAction: "none" }}
                    onMouseDown={(e) => handleMouseDown(e, option)}
                    onTouchStart={(e) => handleTouchStart(e, option, e.currentTarget)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    <div className="mc-fib-opt-media" onClick={() => handleSelectOption(option)}>
                      <ShowMaterialMediaByContentType
                        key={`${uniqueValue}-${index}`}
                        contentType={resolveMaterialMediaType(option, contentMap.type)}
                        src={option}
                        canFullScreen={true}
                      />
                    </div>
                  </div>
                );
              }

              // TEXT word button
              return (
                <div
                  key={index}
                  ref={isDragging ? dragElementRef : null}
                  className="mc-mat-drag-item"
                  style={{ touchAction: "none" }}
                  onMouseDown={(e) => handleMouseDown(e, option)}
                  onTouchStart={(e) => handleTouchStart(e, option, e.currentTarget)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <button
                    type="button"
                    className={`fitb2-word${isUsed ? " used" : ""}${isPicked ? " is-picked" : ""}${isDragging ? " is-picked" : ""}`}
                    onClick={() => !isUsed && handleSelectOption(option)}
                  >
                    <InputWithSpecialExpression value={option} showSpecialOnly={false} />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Hint */}
          <p className="fitb2-hint">
            {i18n.t("fill_in_the_blanks_hint") || i18n.t("please_select_fill_in_the_blanks_text")}
          </p>
        </>
      )}
    </div>
  );
};

export default FillInTheBlanksActivityMaterialContent;
